import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Workflow {
  id: string
  name: string
  type: string
  steps: number
  avg_duration: number
  executions_today: number
  success_rate: number
  status: string
}

interface Automation {
  id: string
  name: string
  trigger: string
  actions: string[]
  runs_24h: number
  avg_execution_time: number
  savings_estimate: number
}

interface Statistics {
  total_workflows: number
  active_automations: number
  executions_today: number
  success_rate: number
  time_saved_hours: number
  cost_savings: number
}

export default function ProcessAutomationPage() {
  const [workflows, setWorkflows] = useState<Workflow[]>([])
  const [automations, setAutomations] = useState<Automation[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [workflowsRes, automationsRes, statsRes] = await Promise.all([
        fetch('/api/processautomation/workflows'),
        fetch('/api/processautomation/automations'),
        fetch('/api/processautomation/statistics')
      ])
      if (workflowsRes.ok) setWorkflows(await workflowsRes.json())
      if (automationsRes.ok) setAutomations(await automationsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching process automation data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value)
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Process Automation</h1>
          <p className="mt-2 text-gray-600">Phase 251 - Business process automation and workflows</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Workflows</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_workflows}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Active Automations</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.active_automations}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Executions Today</p>
              <p className="text-2xl font-bold text-purple-600">{formatNumber(statistics.executions_today)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Success Rate</p>
              <p className="text-2xl font-bold text-green-600">{statistics.success_rate}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Time Saved</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.time_saved_hours}h</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Cost Savings</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(statistics.cost_savings)}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Automated Workflows</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Workflow</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Steps</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Duration</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Executions</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Success Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {workflows.map((workflow) => (
                  <tr key={workflow.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{workflow.name}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">{workflow.type}</span>
                    </td>
                    <td className="px-6 py-4 text-gray-500">{workflow.steps}</td>
                    <td className="px-6 py-4 text-gray-500">{workflow.avg_duration}s</td>
                    <td className="px-6 py-4 text-blue-600 font-medium">{formatNumber(workflow.executions_today)}</td>
                    <td className="px-6 py-4 text-green-600 font-bold">{workflow.success_rate}%</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${workflow.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{workflow.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Automation Rules</h2>
          </div>
          <div className="p-6 space-y-4">
            {automations.map((automation) => (
              <div key={automation.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{automation.name}</span>
                  <span className="text-lg font-bold text-green-600">{formatCurrency(automation.savings_estimate)}</span>
                </div>
                <p className="text-sm text-gray-500 mb-3">Trigger: {automation.trigger}</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {automation.actions.map((action, idx) => (
                    <span key={idx} className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">{action}</span>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Runs (24h)</p>
                    <p className="font-medium text-blue-600">{automation.runs_24h}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Avg Time</p>
                    <p className="font-medium text-purple-600">{automation.avg_execution_time}s</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  )
}
